import {
  ADD_WARNING_REPORT_RECIPIENTS_ASYNC,
  DELETE_WARNING_REPORT_RECIPIENTS_ASYNC,
  GET_DEEPFAKE_CONFIG_ASYNC,
  GET_DEEPFAKE_WIZARD_CONFIG_ASYNC,
  GET_WARNING_REPORT_CANDIDATES_ASYNC,
  GET_WARNING_REPORT_RECIPIENTS_ASYNC,
  SET_DEEPFAKE_WARNING_REPORT_RECIPIENT,
  SET_PERMISSIONS_GRANTED,
  UPDATE_DEEPFAKE_CONFIG_ASYNC,
  UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC,
  VERIFY_CONSENT_ASYNC,
} from './constants'

export default function () {
  const initialState = {
    switchersLoader: false,
    warningReportRecipients: {
      items: [],
      loader: false,
      error: null,
    },
    candidatesLoader: false,
    candidates: [],
    selectedRecipientId: null,
    warningReportRecipientsLoader: false,
    // Consent management fields
    consentText: '',
    consentLink: '',
    consentTimeoutAmountInSeconds: 0,
    consentType: 'implicit',
    consentData: null,
    // Wizard config
    wizardConfig: {
      data: null,
      loader: false,
      error: null,
    },
    // Verify consent
    verifyConsent: {
      data: null,
      loader: false,
      error: null,
    },
    // Graph API permissions state
    permissionsGranted: false,
    joinExternalMeetings: false,
    emailAlertsForInternalsInExternalMeetings: false,
    popupAudience: 'all_internals',
    // MFA Challenge fields
    mfaEnabled: false,
    mfaLinkTimeoutSeconds: 120,
    mfaGlobalTimerSeconds: 420,
    mfaSuspicionAlertEnabled: false,
    mfaFailureAlertEnabled: false,
    // SIEM events field
    siemEventsEnabled: false,
  }

  return (state = initialState, action) => {
    switch (action.type) {
      case GET_DEEPFAKE_CONFIG_ASYNC.INITIATE:
        return { ...state, switchersLoader: true }
      case GET_DEEPFAKE_CONFIG_ASYNC.SUCCESS: {
        const cs = action.payload.data.companySettings
        const allowedPopupAudience = ['organizer_only', 'off', 'all_internals']
        let popupAudience = 'all_internals'
        if (cs?.popupAudience && allowedPopupAudience.includes(cs.popupAudience)) {
          popupAudience = cs.popupAudience
        } else if (cs?.showPopup === false) {
          popupAudience = 'off'
        } else if (cs?.showPopup === true) {
          popupAudience = 'all_internals'
        }
        return {
          ...state,
          switchersLoader: false,
          popupAudience,
          sendWarningReportNotification:
            action.payload.data.companySettings?.sendWarningReportNotification || false,
          liveDeepfakeAlert: action.payload.data.companySettings?.liveDeepfakeAlert || false,
          // Consent management fields from companySettings
          consentText: action.payload.data.companySettings?.consentText || '',
          consentLink: action.payload.data.companySettings?.consentLink || '',
          consentTimeoutAmountInSeconds:
            action.payload.data.companySettings?.consentTimeoutAmountInSeconds || 0,
          consentType: action.payload.data.companySettings?.consentType || 'implicit',
          consentData: action.payload.data.companySettings?.consentData || null,
          joinExternalMeetings: action.payload.data.companySettings?.joinExternalMeetings || false,
          emailAlertsForInternalsInExternalMeetings:
            action.payload.data.companySettings?.emailAlertsForInternalsInExternalMeetings || false,
          mfaEnabled: action.payload.data.companySettings?.mfaEnabled || false,
          mfaLinkTimeoutSeconds: action.payload.data.companySettings?.mfaLinkTimeoutSeconds || 120,
          mfaGlobalTimerSeconds: action.payload.data.companySettings?.mfaGlobalTimerSeconds || 420,
          mfaSuspicionAlertEnabled:
            action.payload.data.companySettings?.mfaSuspicionAlertEnabled || false,
          mfaFailureAlertEnabled:
            action.payload.data.companySettings?.mfaFailureAlertEnabled || false,
          siemEventsEnabled: action.payload.data.companySettings?.siemEventsEnabled || false,
        }
      }
      case GET_DEEPFAKE_CONFIG_ASYNC.ERROR:
        return {
          ...state,
          switchersLoader: false,
        }
      case GET_DEEPFAKE_CONFIG_ASYNC.CANCEL:
        return { ...state, switchersLoader: false }
      case UPDATE_DEEPFAKE_CONFIG_ASYNC.INITIATE:
        // Handle consentDataSettings update - merge into existing consentData
        if (action.payload.key === 'consentDataSettings') {
          const { consentType, text, link } = action.payload.value
          const currentConsentData = state.consentData || {}
          // Preserve existing timeout if it exists
          const existingTimeout = currentConsentData[consentType]?.timeout || null
          return {
            ...state,
            popupAudience: state.popupAudience,
            sendWarningReportNotification: state.sendWarningReportNotification,
            liveDeepfakeAlert: state.liveDeepfakeAlert,
            consentData: {
              ...currentConsentData,
              [consentType]: {
                text,
                link: link || null,
                timeout: existingTimeout,
              },
            },
          }
        }
        if (action.payload.key === 'mfaLinkTimeoutSeconds') {
          // Coupling: total challenge window is link × 3 attempts + 60s spare
          // buffer (matches MFA_GLOBAL_SPARE_SECONDS in DFS). Update both
          // fields here so the epic sees them together in state and sends a
          // single consistent PUT body (avoids the 400 race that happened when
          // two separate actions were dispatched).
          return {
            ...state,
            popupAudience: state.popupAudience,
            sendWarningReportNotification: state.sendWarningReportNotification,
            liveDeepfakeAlert: state.liveDeepfakeAlert,
            mfaLinkTimeoutSeconds: action.payload.value,
            mfaGlobalTimerSeconds: action.payload.value * 3 + 60,
          }
        }
        return {
          ...state,
          popupAudience: state.popupAudience,
          sendWarningReportNotification: state.sendWarningReportNotification,
          liveDeepfakeAlert: state.liveDeepfakeAlert,
          // Handle consent field updates
          [action.payload.key]:
            action.payload.value !== undefined ? action.payload.value : !state[action.payload.key],
        }
      case UPDATE_DEEPFAKE_CONFIG_ASYNC.SUCCESS:
        return state
      case GET_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE:
        return {
          ...state,
          warningReportRecipients: {
            ...state.warningReportRecipients,
            loader: true,
            error: null,
          },
        }
      case GET_WARNING_REPORT_RECIPIENTS_ASYNC.SUCCESS:
        return {
          ...state,
          warningReportRecipients: {
            ...state.warningReportRecipients,
            loader: false,
            ...action.payload.data,
          },
        }
      case GET_WARNING_REPORT_RECIPIENTS_ASYNC.ERROR:
        return {
          ...state,
          warningReportRecipients: {
            ...state.warningReportRecipients,
            loader: false,
            error: action.error,
          },
        }
      case GET_WARNING_REPORT_CANDIDATES_ASYNC.INITIATE:
        return {
          ...state,
          candidatesLoader: true,
        }
      case GET_WARNING_REPORT_CANDIDATES_ASYNC.SUCCESS:
        return {
          ...state,
          candidatesLoader: false,
          candidates: action.payload.data.items,
        }
      case GET_WARNING_REPORT_CANDIDATES_ASYNC.ERROR:
        return {
          ...state,
          candidatesLoader: false,
        }
      case SET_DEEPFAKE_WARNING_REPORT_RECIPIENT:
        return {
          ...state,
          selectedRecipientId: action.payload.id,
        }
      case ADD_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE:
      case DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE:
        return {
          ...state,
          warningReportRecipientsLoader: true,
        }
      case ADD_WARNING_REPORT_RECIPIENTS_ASYNC.SUCCESS:
      case ADD_WARNING_REPORT_RECIPIENTS_ASYNC.ERROR:
      case DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.SUCCESS:
      case DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.ERROR:
        return {
          ...state,
          warningReportRecipientsLoader: false,
        }
      // Wizard config cases
      case UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.INITIATE:
      case GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.INITIATE:
        return {
          ...state,
          wizardConfig: {
            ...state.wizardConfig,
            loader: true,
            error: null,
          },
        }
      case GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.SUCCESS:
        return {
          ...state,
          wizardConfig: {
            ...state.wizardConfig,
            data: action.payload.data,
            loader: false,
            error: null,
          },
        }
      case UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.SUCCESS:
        return {
          ...state,
          wizardConfig: {
            ...state.wizardConfig,
            data: {
              ...state.wizardConfig.data,
              ...action.payload.data,
            },
            loader: false,
            error: null,
          },
        }
      case UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.ERROR:
      case GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.ERROR:
        return {
          ...state,
          wizardConfig: {
            ...state.wizardConfig,
            loader: false,
            error: action.error,
          },
        }
      case VERIFY_CONSENT_ASYNC.INITIATE:
        return {
          ...state,
          verifyConsent: {
            ...state.verifyConsent,
            loader: true,
            error: null,
          },
        }
      case VERIFY_CONSENT_ASYNC.SUCCESS:
        return {
          ...state,
          verifyConsent: {
            ...state.verifyConsent,
            data: action.payload.data,
            loader: false,
            error: null,
          },
        }
      case VERIFY_CONSENT_ASYNC.ERROR:
        return {
          ...state,
          verifyConsent: {
            ...state.verifyConsent,
            loader: false,
            error: action.error,
          },
        }
      case SET_PERMISSIONS_GRANTED:
        return {
          ...state,
          permissionsGranted: action.payload.granted,
        }

      default:
        return state
    }
  }
}
